// Several ports described in one act (#473, ZENITH.md §11.3): an editor
// whose every field starts *unchanged*, applied to the ports the faceplate
// gathered (#467), previewed port by port, and written as one whole-set
// `PUT /switches/{serial}/ports` — the write §11.3 already defines, with
// every port's generation carried and the ports not selected sent byte for
// byte as they were read. Nothing here reaches a controller: the operator
// plans and applies afterwards exactly as before.
//
// **Unchanged is a state of the editor, not of the API.** The request has no
// partial verb, so a field left unchanged is composed here from what each
// port already says: the port's own value where it has intent, and **what
// the cell holds for the port** where it is described for the first time
// (#571, ADR-0078 decision 5) — a first intent composed from blanks is the
// port's whole intent, and the next apply replaced the port's VLANs and PoE
// with it. The held fields are the page's read (`Held` in `Ports.tsx`), asked
// for once an edit is under way on a port with no intent. Where they were not
// read — and on a planned switch, which has no held document (#521) — the
// first intent starts from blanks as before, and the preview says which
// ports those are and what saving them means. A port with no intent and
// nothing held needs a mode — the contract requires it — so the editor holds
// the apply until one is chosen and names the ports rather than guessing
// `access`; a held port's mode is the contract's own rule applied to what it
// holds, a trunk if it holds a tagged VLAN.
//
// **VLANs are chosen by the customer's names** (ADR-0064). The chooser reads
// the site's tag space, `GET /sites/{id}/vlans`, and groups the tags by
// name — a name held by several tags is one group — then lists the tags a
// port or a network uses that nobody has named, then takes a number for a
// tag that is neither. The number is what is written; the name is a label
// beside it and enters no request (decision 3). Whether the switch has an
// interface for the tag stays the plan's question, not the chooser's.
//
// **A name group is chosen whole** (#590). A customer whose *Rooms* is
// 3000–4000 has a thousand and one tags under one name, and a port that
// carries the rooms carries all of them: the chooser lists the group as one
// chip — the name, its runs and its count — and pressing it adds every tag
// as one act; the chosen set shows the group as one chip whose × removes
// every tag of it, and the preview reads *Rooms (1001)* rather than the
// numbers. The tags of a group stay reachable behind the chip for a port
// that wants some and not all. What is written is unchanged — a thousand
// and one integers in `tagged_vlans` — and so is what the plan does with
// them: one `intf_<id>` per tag (ADR-0066), or a refusal where a tag falls
// inside a range interface's span. The hint under the chooser says so, and
// this component does not soften it; a range in the switch's own shape is
// an amendment to ADR-0066 and its own item.
//
// `composeSet` is pure, so a test can hold the preview the editor shows
// against the set it sends: the two are one computation.
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { api } from "../../api/client";
import { SKIP, useQuery } from "../../api/hooks";
import type { Problem } from "../../api/problem";
import type { useMutation } from "../../api/mutation";
import type { components } from "../../api/schema";
import { plural } from "../../dashboard/format";
import { Info, Pill, ProblemBox } from "../../ui";
import { PortName, byNumber } from "./DeviceFace";
import { nameGroups, runsWords, setByName, setWords, spaceOf, type NameGroup, type SiteVlan, type Space } from "../networks/vlanModel";
import type { Held } from "./Ports";
import { ClosedIcon, Glyph, OpenIcon } from "../../icons";
import { echoed } from "../../api/openEnum";

type PortIntent = components["schemas"]["PortIntent"];
type PortSet = components["schemas"]["PortIntentSet"];
type Entry = components["schemas"]["PortIntentEntry"];
type Mode = components["schemas"]["SwitchPortMode"];
type Priority = components["schemas"]["PoEPriority"];
type HeldFields = components["schemas"]["SwitchPortFields"];

/** A field of the editor: left alone, cleared, or set to a value — `set`
 * with no value yet is a field the operator opened and has not filled, and
 * the apply waits for it. */
export type Tri<T> = { kind: "unchanged" } | { kind: "clear" } | { kind: "set"; value?: T };
/** The tagged set's verbs: the common act is one more VLAN on every trunk,
 * which *replace* alone cannot say without retyping each port's list. */
export type TaggedVerb = "replace" | "add" | "remove";

export interface BulkEdit {
  mode: Tri<Mode>;
  untagged: Tri<number>;
  tagged: { kind: "unchanged" } | { kind: TaggedVerb; vlans: number[] };
  /** `clear` sends no `poe.enabled` at all, so the port is sent with the
   * setting the cell holds — the single editor's blank, and not the same as
   * off (ADR-0078 decision 1). */
  poe: Tri<boolean>;
  poePriority: Tri<Priority>;
  poeLimit: Tri<number>;
  description: Tri<string>;
  enabled: Tri<boolean>;
}

const U = { kind: "unchanged" } as const;
export const EMPTY_EDIT: BulkEdit = { mode: U, untagged: U, tagged: U, poe: U, poePriority: U, poeLimit: U, description: U, enabled: U };

export const isEmptyEdit = (e: BulkEdit): boolean => Object.values(e).every((f) => (f as { kind: string }).kind === "unchanged");

/** The modelled fields a preview compares, in the order the table shows them. */
export const FIELDS = ["mode", "untagged_vlan", "tagged_vlans", "poe.enabled", "poe.priority", "poe.power_limit_mw", "description", "enabled"] as const;
export type Field = (typeof FIELDS)[number];

/** One port of the preview: what it says now, what it would say, and which
 * fields differ. `firstTime` is a port with no intent yet; `needsMode` is
 * such a port while the editor's mode is unchanged. */
export interface PreviewRow {
  port: string;
  before?: PortIntent;
  after?: Entry;
  differs: Field[];
  firstTime: boolean;
  needsMode: boolean;
  /** A first intent composed from what the cell holds for the port (#571);
   * false on a first-time port is one composed from blanks. */
  fromHeld: boolean;
}

/** The request entry for a port that already has intent — the same fields,
 * with the generation it was read at. The single editor's `entryOf`, kept
 * beside the composition that relies on it being byte-faithful. */
export function entryOf(p: PortIntent): Entry {
  return {
    port: p.port, mode: echoed<Mode>(p.mode), untagged_vlan: p.untagged_vlan, tagged_vlans: p.tagged_vlans,
    ...(p.poe ? { poe: poeEntry(p.poe) } : {}), ...(p.description ? { description: p.description } : {}),
    // The edge port an operator set (ADR-0426 decision 3a) is carried back as
    // read, so a write about anything else does not drop it.
    ...(p.edge_port !== undefined ? { edge_port: p.edge_port } : {}),
    enabled: p.enabled, generation: p.generation,
  };
}

/** A port's PoE as the intent read holds it, sent back as received (ADR-0511 4a, `echoed`). */
export function poeEntry(poe: NonNullable<PortIntent["poe"]>): NonNullable<Entry["poe"]> {
  const { priority, ...rest } = poe;
  return priority === undefined ? rest : { ...rest, priority: echoed<Priority>(priority) };
}

const sorted = (v: Iterable<number>) => [...new Set(v)].sort((a, b) => a - b);

const PRIORITIES_HELD: readonly string[] = ["critical", "high", "medium", "low"];

/** The entry a port with no intent starts from: its modelled fields as the
 * cell holds them (#571). The mode is the contract's rule applied to them. A
 * priority the document spells in a word the intent cannot carry is left
 * out, which the single editor names; here it shows as no priority in the
 * preview. An `enabled` the document does not say is the editor's default. */
export function heldEntry(port: string, f: HeldFields): Entry {
  const poe: NonNullable<Entry["poe"]> = {};
  if (f.poe?.enabled !== undefined) poe.enabled = f.poe.enabled;
  if (f.poe?.priority !== undefined && PRIORITIES_HELD.includes(f.poe.priority)) poe.priority = f.poe.priority as Priority;
  if (f.poe?.power_limit_mw !== undefined) poe.power_limit_mw = f.poe.power_limit_mw;
  return {
    port, mode: f.tagged_vlans.length > 0 ? "trunk" : "access",
    ...(f.untagged_vlan !== undefined ? { untagged_vlan: f.untagged_vlan } : {}), tagged_vlans: f.tagged_vlans,
    ...(Object.keys(poe).length ? { poe } : {}), ...(f.description ? { description: f.description } : {}),
    enabled: f.enabled ?? true,
  };
}

/** The entry the edit makes of one port. Undefined when the port has no
 * intent and the edit names no mode: there is nothing the contract would
 * accept, and the caller says so rather than inventing one. */
export function applyEdit(edit: BulkEdit, port: string, current?: PortIntent, held?: HeldFields): Entry | undefined {
  const start = !current && held ? heldEntry(port, held) : undefined;
  const mode = (edit.mode.kind === "set" && edit.mode.value) || (current ? echoed<Mode>(current.mode) : undefined) || start?.mode;
  if (!mode) return undefined;
  const base: Entry = current ? entryOf(current) : start ?? { port, mode, tagged_vlans: [], enabled: true };
  const next: Entry = { ...base, mode };
  if (edit.untagged.kind === "set" && edit.untagged.value !== undefined) next.untagged_vlan = edit.untagged.value;
  else if (edit.untagged.kind === "clear") delete next.untagged_vlan;
  if (edit.tagged.kind === "replace") next.tagged_vlans = sorted(edit.tagged.vlans);
  else if (edit.tagged.kind === "add") next.tagged_vlans = sorted([...(base.tagged_vlans ?? []), ...edit.tagged.vlans]);
  else if (edit.tagged.kind === "remove") { const out = new Set(edit.tagged.vlans); next.tagged_vlans = (base.tagged_vlans ?? []).filter((v) => !out.has(v)); }
  const poe: NonNullable<Entry["poe"]> = { ...(base.poe ?? {}) };
  if (edit.poe.kind === "set" && edit.poe.value !== undefined) poe.enabled = edit.poe.value; else if (edit.poe.kind === "clear") delete poe.enabled;
  if (edit.poePriority.kind === "set" && edit.poePriority.value) poe.priority = edit.poePriority.value; else if (edit.poePriority.kind === "clear") delete poe.priority;
  if (edit.poeLimit.kind === "set" && edit.poeLimit.value !== undefined) poe.power_limit_mw = edit.poeLimit.value; else if (edit.poeLimit.kind === "clear") delete poe.power_limit_mw;
  if (Object.keys(poe).length) next.poe = poe; else delete next.poe;
  if (edit.description.kind === "set" && edit.description.value) next.description = edit.description.value; else if (edit.description.kind === "clear") delete next.description;
  if (edit.enabled.kind === "set" && edit.enabled.value !== undefined) next.enabled = edit.enabled.value;
  return next;
}

const same = (a: unknown, b: unknown) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);

/** The fields of `after` that differ from `before`, among those Zenith models. */
export function differing(before: PortIntent | undefined, after: Entry): Field[] {
  const b = before ? entryOf(before) : undefined;
  const out: Field[] = [];
  if (!same(b?.mode, after.mode)) out.push("mode");
  if (!same(b?.untagged_vlan, after.untagged_vlan)) out.push("untagged_vlan");
  if (!same(b?.tagged_vlans ?? [], after.tagged_vlans)) out.push("tagged_vlans");
  if (!same(b?.poe?.enabled, after.poe?.enabled)) out.push("poe.enabled");
  if (!same(b?.poe?.priority, after.poe?.priority)) out.push("poe.priority");
  if (!same(b?.poe?.power_limit_mw, after.poe?.power_limit_mw)) out.push("poe.power_limit_mw");
  if (!same(b?.description, after.description)) out.push("description");
  if (!same(b?.enabled ?? true, after.enabled ?? true)) out.push("enabled");
  return out;
}

/** The whole set the PUT sends and the preview that shows it: every port
 * with intent carried as read, the selected ports rewritten by the edit,
 * in the switch's order. `entries` is what is sent; `rows` is what is shown
 * for the selection; `needMode` names the selected ports that cannot be
 * described until the edit says a mode. */
export function composeSet(set: PortSet, picked: readonly string[], edit: BulkEdit, held?: Record<string, HeldFields>): { entries: Entry[]; rows: PreviewRow[]; needMode: string[]; changed: number } {
  const byName = new Map(set.ports.map((p) => [p.port, p]));
  const chosen = new Set(picked);
  const rows: PreviewRow[] = [...picked].sort(byNumber).map((port) => {
    const before = byName.get(port);
    const after = applyEdit(edit, port, before, held?.[port]);
    return { port, before, after, differs: after ? differing(before, after) : [], firstTime: !before, needsMode: !after, fromHeld: !before && !!held?.[port] };
  });
  const rewritten = new Map(rows.filter((r) => r.after).map((r) => [r.port, r.after!]));
  const entries = [
    ...set.ports.filter((p) => !chosen.has(p.port)).map(entryOf),
    ...rewritten.values(),
  ].sort((a, b) => byNumber(a.port, b.port));
  return { entries, rows, needMode: rows.filter((r) => r.needsMode).map((r) => r.port), changed: rows.filter((r) => r.differs.length > 0).length };
}

/** The tags of a site's space, grouped as the chooser lists them: one group
 * per name (a name several tags share is one group, its tags ascending and
 * their runs beside them), names in natural order; then the tags something
 * uses that nobody named. */
export function groupedTags(space: Space): { named: NameGroup[]; unnamed: SiteVlan[] } {
  const unnamed: SiteVlan[] = [];
  for (const tag of space.present) {
    const v = space.byTag.get(tag)!;
    if (!v.name && (v.ports.length > 0 || v.networks.length > 0)) unnamed.push(v);
  }
  return { named: nameGroups(space), unnamed };
}

/** What a tag references, in words: "2 ports, 1 network". */
function referenceWords(v: SiteVlan): string {
  const parts = [];
  if (v.ports.length) parts.push(`${v.ports.length} ${plural(v.ports.length, "port")}`);
  if (v.networks.length) parts.push(`${v.networks.length} ${plural(v.networks.length, "network")}`);
  return parts.join(", ");
}

/** The site's tag space, read once for the editor and kept while it is on
 * screen. A read that fails leaves the chooser with a number field alone
 * and the reason beside it; nothing about the write depends on it. With no
 * site to ask for, nothing is read — the caller already holds the space. */
export function useTagSpace(siteId: string | undefined): TagSpace {
  const q = useQuery(() => (siteId ? api.GET("/sites/{id}/vlans", { params: { path: { id: siteId } } }) : SKIP), [siteId]);
  const space = useMemo(() => (q.data ? spaceOf(q.data) : undefined), [q.data]);
  return { status: q.status, problem: q.status === "error" ? q.problem : undefined, space };
}
export interface TagSpace { status: string; problem?: Problem; space?: Space }

/** The tags chosen, as chips, and the ways to choose one: by the customer's
 * name, from the unnamed tags in use, or by number. One tag at a time when
 * `single`; a set otherwise.
 *
 * A name group is one chip. Pressing it adds every tag of the group; a
 * group the set holds in part reads *3 of 1001* and pressing it completes
 * it; a group held whole is chosen, and pressing it again — or the × on its
 * chip among the chosen — removes every tag of it. The tags behind a group
 * are shown on request, for a port that wants some and not all. In `single`
 * mode a group of more than one tag is not choosable and says why; its tags
 * unfold the same way, so one of them can be. */
export function VlanChooser({ id, value, single, onChange, space, status }: {
  id: string;
  value: number[];
  single?: boolean;
  onChange(next: number[]): void;
  space?: Space;
  status: string;
}) {
  const [typed, setTyped] = useState("");
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState<readonly string[]>([]);
  const groups = useMemo(() => (space ? groupedTags(space) : { named: [], unnamed: [] }), [space]);
  const held = useMemo(() => new Set(value), [value]);
  const chosen = useMemo(() => setByName(space, value), [space, value]);
  const has = (tag: number) => held.has(tag);
  const pick = (tag: number) => onChange(single ? [tag] : has(tag) ? value.filter((v) => v !== tag) : sorted([...value, tag]));
  const pickGroup = (g: NameGroup) => onChange(sorted([...value, ...g.tags]));
  const dropGroup = (g: NameGroup) => { const out = new Set(g.tags); onChange(value.filter((v) => !out.has(v))); };
  const addTyped = () => {
    const n = Number(typed);
    if (!Number.isInteger(n) || n < 1 || n > 4094) return;
    pick(n); setTyped("");
  };
  const label = (tag: number) => { const name = space?.byTag.get(tag)?.name; return name ? `${tag} — ${name}` : String(tag); };
  const tagChip = (tag: number, title?: string) => (
    <button key={tag} type="button" className={"vlan-chip" + (has(tag) ? " is-chosen" : "")} aria-pressed={has(tag)} title={title} onClick={() => pick(tag)}><code>{tag}</code></button>
  );
  return (
    <div className="vlan-chooser" id={id}>
      <div className="vlan-chosen">
        {value.length === 0 && <span className="muted small">{single ? "No VLAN chosen." : "No VLANs chosen."}</span>}
        {chosen.groups.map((g) => (
          <button key={"group:" + g.name} type="button" className="vlan-chip vlan-chip-group is-chosen" aria-label={`Remove ${g.name} — ${g.tags.length} VLANs`} title={`Remove every VLAN of ${g.name}: ${runsWords(g.tags)}`} onClick={() => dropGroup(g)}>
            <span className="vlan-chip-name">{g.name}</span><code>{runsWords(g.tags)}</code><span className="vlan-chip-count">{g.tags.length} VLANs</span> ×
          </button>
        ))}
        {chosen.rest.map((tag) => (
          <button key={tag} type="button" className="vlan-chip is-chosen" aria-label={`Remove ${label(tag)}`} title="Remove" onClick={() => onChange(value.filter((v) => v !== tag))}>
            <code>{tag}</code>{space?.byTag.get(tag)?.name && <span className="vlan-chip-name">{space.byTag.get(tag)!.name}</span>} ×
          </button>
        ))}
        <button type="button" className="btn btn-quiet" aria-expanded={open} onClick={() => setOpen(!open)}>{open ? "Done" : single ? "Choose a VLAN" : "Choose VLANs"}</button>
      </div>
      {open && (
        <div className="vlan-groups" role="group" aria-label={single ? "Choose a VLAN" : "Choose VLANs"}>
          {status === "loading" && <p className="muted small">Loading the site&rsquo;s VLAN names&hellip;</p>}
          {status === "error" && <p className="muted small">The site&rsquo;s VLAN names could not be read. You can still type a VLAN number.</p>}
          {groups.named.length > 0 && (
            <div className="vlan-group"><span className="vlan-group-head">By name</span>
              {single && groups.named.some((g) => g.tags.length > 1) && <span className="muted small">An untagged port takes one VLAN: open a group and choose one of its VLANs.</span>}
              {groups.named.map((g) => {
                const one = g.tags.length === 1;
                const n = g.tags.reduce((k, t) => k + (has(t) ? 1 : 0), 0);
                const whole = n === g.tags.length;
                const barred = !!single && !one;
                const expanded = !one && shown.includes(g.name);
                const count = one ? undefined : whole ? `${g.tags.length} VLANs` : n > 0 ? `${n} of ${g.tags.length}` : `${g.tags.length} VLANs`;
                const title = one ? undefined
                  : barred ? "An untagged port takes one VLAN: open the group and choose one of its VLANs."
                    : whole ? `Remove every VLAN of ${g.name}`
                      : n > 0 ? `Add the ${g.tags.length - n} VLANs of ${g.name} not chosen yet` : `Add every VLAN of ${g.name} — ${g.tags.length} of them`;
                return (
                  <div key={g.name} className="vlan-name-group">
                    <button type="button" className={"vlan-chip vlan-chip-group" + (whole ? " is-chosen" : n > 0 ? " is-partial" : "")}
                      aria-pressed={whole} aria-label={one ? `${g.name} — ${g.tags[0]}` : `${g.name} — ${runsWords(g.tags)} — ${count}`} disabled={barred} title={title}
                      onClick={() => (one ? pick(g.tags[0]!) : whole ? dropGroup(g) : pickGroup(g))}>
                      <span className="vlan-chip-name">{g.name}</span><code>{runsWords(g.tags)}</code>{count && <span className="vlan-chip-count">{count}</span>}
                    </button>
                    {!one && (
                      <button type="button" className="btn btn-quiet vlan-expand" aria-expanded={expanded} aria-label={`${expanded ? "Hide" : "Show"} the VLANs of ${g.name}`}
                        onClick={() => setShown(expanded ? shown.filter((x) => x !== g.name) : [...shown, g.name])}><Glyph icon={expanded ? OpenIcon : ClosedIcon} /></button>
                    )}
                    {expanded && <span className="vlan-tags">{g.tags.map((tag) => tagChip(tag))}</span>}
                  </div>
                );
              })}
            </div>
          )}
          {groups.unnamed.length > 0 && (
            <div className="vlan-group"><span className="vlan-group-head">Unnamed, in use at this site</span>
              <div className="vlan-tags">
                {groups.unnamed.map((v) => tagChip(v.vlan, referenceWords(v)))}
              </div>
            </div>
          )}
          {status === "ready" && groups.named.length === 0 && groups.unnamed.length === 0 && <p className="muted small">The customer has named no VLAN, and nothing at this site uses one.</p>}
          <div className="vlan-group"><span className="vlan-group-head">Another VLAN<Info label="About a VLAN number">The number is what is saved. A name is only a label. If the switch has no interface for a VLAN, the configuration plan creates one: one interface per VLAN, named intf_ and the number, in the shape of the switch&apos;s other VLAN interfaces. If a VLAN falls inside one of the switch&apos;s range interfaces, the configuration plan is refused. Choosing a group of a thousand VLANs means a thousand interfaces, or a refusal. The configuration plan says which, before anything is applied.</Info></span>
            <div className="field-row">
              <input type="number" min={1} max={4094} aria-label="VLAN number" value={typed} placeholder="1–4094"
                onChange={(e) => setTyped(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTyped(); } }} />
              <button type="button" className="btn" onClick={addTyped} disabled={!typed}>{single ? "Use it" : "Add it"}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const PRIORITIES: Priority[] = ["critical", "high", "medium", "low"];

/** One field's state selector: unchanged, cleared, or set — followed by the
 * control for the value when set. */
function TriField<T>({ label, field, onChange, clearLabel, children, hint }: {
  label: string;
  field: Tri<T>;
  onChange(next: Tri<T>): void;
  /** The words for `clear`; omitted, the field has no clear state. */
  clearLabel?: string;
  /** The control shown while the field is `set`. */
  children?: ReactNode;
  hint?: string;
}) {
  const kind = field.kind;
  return (
    <div className="field bulk-field">
      <span className="label">{label}{hint && <Info label={`About ${label}`}>{hint}</Info>}
        <select aria-label={`${label}: how`} value={kind} onChange={(e) => {
          const k = e.target.value;
          onChange(k === "unchanged" ? { kind: "unchanged" } : k === "clear" ? { kind: "clear" } : { kind: "set", value: field.kind === "set" ? field.value : undefined });
        }}>
          <option value="unchanged">unchanged</option>
          {clearLabel && <option value="clear">{clearLabel}</option>}
          <option value="set">set to…</option>
        </select>
      </span>
      {kind === "set" && children}
    </div>
  );
}

/** A `set` value, or nothing while the operator has not typed one yet. */
const setValue = <T,>(f: Tri<T>): T | undefined => (f.kind === "set" ? f.value : undefined);

/** The words in a preview cell for a modelled field, before and after. */
function cellWords(field: Field, e?: Entry | PortIntent, space?: Space): string {
  if (!e) return "—";
  switch (field) {
    case "mode": return e.mode;
    case "untagged_vlan": return e.untagged_vlan === undefined ? "—" : `${e.mode === "trunk" ? "native" : "access"} ${e.untagged_vlan}`;
    case "tagged_vlans": return e.tagged_vlans?.length ? setWords(space, e.tagged_vlans) : "—";
    case "poe.enabled": return e.poe?.enabled === undefined ? "as held" : e.poe.enabled ? "on" : "off";
    case "poe.priority": return e.poe?.priority ?? "—";
    case "poe.power_limit_mw": return e.poe?.power_limit_mw === undefined ? "—" : `${e.poe.power_limit_mw / 1000} W`;
    case "description": return e.description ?? "—";
    case "enabled": return (e.enabled ?? true) ? "yes" : "no";
  }
}

const FIELD_LABEL: Record<Field, string> = {
  mode: "Mode", untagged_vlan: "Untagged / native", tagged_vlans: "Tagged", "poe.enabled": "PoE", "poe.priority": "PoE priority",
  "poe.power_limit_mw": "PoE limit", description: "Description", enabled: "Enabled",
};

/** The value the editor holds for a field that is set and has none yet —
 * a mode not chosen, a number not typed — cannot be composed; the apply
 * waits for it the way it waits for a mode. */
function incomplete(edit: BulkEdit): string[] {
  const out: string[] = [];
  if (edit.mode.kind === "set" && !edit.mode.value) out.push("Mode");
  if (edit.untagged.kind === "set" && !edit.untagged.value) out.push("Untagged VLAN");
  if (edit.tagged.kind !== "unchanged" && edit.tagged.vlans.length === 0) out.push("Tagged VLANs");
  if (edit.poe.kind === "set" && edit.poe.value === undefined) out.push("PoE");
  if (edit.poePriority.kind === "set" && !edit.poePriority.value) out.push("PoE priority");
  if (edit.poeLimit.kind === "set" && !(edit.poeLimit.value !== undefined && edit.poeLimit.value > 0)) out.push("PoE power limit");
  if (edit.description.kind === "set" && !edit.description.value) out.push("Description");
  if (edit.enabled.kind === "set" && edit.enabled.value === undefined) out.push("Enabled");
  return out;
}

/** What the editor says of itself where a planned switch and a claimed one
 * differ (#521): the verb on the save button — *Save for* a claimed switch's
 * ports, *Describe* a planned one's — and the sentence after "sends nothing
 * to the switch", which on a claimed switch points at the plan and on a
 * planned one at the binding. The form, the composition and the write are
 * the same component either way; only the words move. */
export interface EditorWording { verb: string; afterwards: ReactNode }
// *Save for*, and not *Apply to* as it read until #1678: on these pages *apply* is the act that
// sends a plan's document to a controller (§10.2; ADR-0127 decision 11 makes the same argument for
// *give*), and this button stores planned settings and sends nothing.
const CLAIMED_WORDING: EditorWording = { verb: "Save for", afterwards: <>plan and apply the switch&rsquo;s configuration afterwards, as usual.</> };

export default function PortsEditor({ set, picked, uplink, lane, onApply, onReload, onSelectAll, onClear, drawn, held, onNeedHeld, wording = CLAIMED_WORDING, tags: given, edit: controlled, onEdit }: {
  set: PortSet;
  /** The ports the faceplate gathered, in its order. */
  picked: readonly string[];
  /** Every port the switch reaches its controller through: one, or every
   * member of an aggregate (ADR-0209 decision 1c). */
  uplink?: readonly string[];
  /** The write's own lane, so its refusal is read beside the editor. */
  lane: ReturnType<typeof useMutation>;
  /** Sends the composed set; resolves true when the API accepted it. */
  onApply(entries: Entry[]): Promise<boolean>;
  /** Reads the intent again, which is what a stale refusal asks for. */
  onReload(): void;
  onSelectAll(): void;
  onClear(): void;
  /** How many ports the faceplate draws, for the Select all button's words. */
  drawn: number;
  /** What the cell holds for the switch's ports, which a first intent is
   * composed from (#571). Absent where there is no held document to read: a
   * planned switch. */
  held?: Held;
  /** Asks the page to read what the cell holds. Called once an edit is under
   * way on a port with no intent and nothing has been read. */
  onNeedHeld?(): void;
  wording?: EditorWording;
  /** The site's tag space where the page already reads it (the device
   * page's table shows names from the same read); absent, the editor reads
   * it itself. */
  tags?: TagSpace;
  /** The fields as the page holds them, where the page has a pending bar whose
   * *Discard* drops them (#1893); left out, the editor holds its own. */
  edit?: BulkEdit;
  onEdit?(next: BulkEdit): void;
}) {
  const [own, setOwn] = useState<BulkEdit>(EMPTY_EDIT);
  const edit = controlled ?? own;
  const setEdit = onEdit ?? setOwn;
  const read = useTagSpace(given ? undefined : set.site_id);
  const tags = given ?? read;
  const heldPorts = held?.status === "ready" ? held.ports : undefined;
  const { entries, rows, needMode, changed } = useMemo(() => composeSet(set, picked, edit, heldPorts), [set, picked, edit, heldPorts]);
  const empty = isEmptyEdit(edit);
  const firstTime = rows.some((r) => r.firstTime);
  const wantsHeld = held?.status === "idle" && !empty && firstTime;
  useEffect(() => { if (wantsHeld) onNeedHeld?.(); }, [wantsHeld, onNeedHeld]);
  // A first intent must not be written while what it starts from is still
  // being read: the set composed now is the blank one.
  const readingHeld = firstTime && !empty && (held?.status === "reading" || held?.status === "idle");
  const blankFirst = held && !readingHeld ? rows.filter((r) => r.firstTime && !r.fromHeld).map((r) => r.port) : [];
  const missing = incomplete(edit);
  const trunkish = edit.mode.kind === "set" ? edit.mode.value === "trunk" : rows.length > 0 && rows.every((r) => r.before?.mode === "trunk");
  const canApply = picked.length > 0 && !empty && missing.length === 0 && needMode.length === 0 && changed > 0 && !lane.busy && !readingHeld;
  const apply = async () => {
    if (!canApply) return;
    if (await onApply(entries)) setEdit(EMPTY_EDIT);
  };
  const up = <T,>(k: keyof BulkEdit, v: T) => setEdit({ ...edit, [k]: v });
  return (
    <section className="panel ports-editor" aria-label="Describe the selected ports">
      <div className="page-head"><span className="page-title"><h3>Describe the selected ports</h3><Info label="How to select ports">
          Select ports on the faceplate above, then set only the fields you want to change. A field left <em>unchanged</em> keeps each port&rsquo;s own value.{" "}
          Click a port to select it, or drag a rectangle across several. Shift adds to the selection, Ctrl/⌘ toggles a port, and Esc clears the selection.
        </Info></span>
        <span className="ports-editor-sel">
          <button type="button" className="btn btn-quiet" onClick={onSelectAll} disabled={drawn === 0 || picked.length === drawn}>Select all {drawn} {plural(drawn, "port")}</button>
          <button type="button" className="btn btn-quiet" onClick={onClear} disabled={picked.length === 0}>Clear</button>
        </span>
      </div>
      {/* How the editor is used sits behind the ⓘ beside its heading, and what saving sends behind
          the one beside the save button (#1890, ruled 2026-10-20: copy about what an act sends moves
          behind the ⓘ next to the button it describes), each sentence whole. */}
      <div className="form bulk-form">
        <TriField<Mode> label="Mode" field={edit.mode} onChange={(f) => up("mode", f)}>
          <select aria-label="Mode: value" value={setValue(edit.mode) ?? ""} onChange={(e) => up("mode", { kind: "set", value: e.target.value as Mode })}>
            <option value="">Choose one</option><option value="access">access</option><option value="trunk">trunk</option>
          </select>
        </TriField>
        <TriField<number> label={trunkish ? "Native VLAN" : "Untagged VLAN"} field={edit.untagged} onChange={(f) => up("untagged", f)} clearLabel="none"
          hint="The access VLAN on an access port, the native VLAN on a trunk. At most one.">
          <VlanChooser id="bulk-untagged" single value={setValue(edit.untagged) === undefined ? [] : [setValue(edit.untagged)!]}
            onChange={(v) => up("untagged", v.length ? { kind: "set", value: v[0] } : { kind: "set", value: undefined })} space={tags.space} status={tags.status} />
        </TriField>
        <div className="field bulk-field">
          <span className="label">Tagged VLANs<Info label="About Tagged VLANs">Replace sets each port&rsquo;s list. Add and remove change it. If the switch has no interface for a VLAN, the configuration plan creates one and names it.</Info>
            <select aria-label="Tagged VLANs: how" value={edit.tagged.kind} onChange={(e) => {
              const k = e.target.value as "unchanged" | TaggedVerb;
              up("tagged", k === "unchanged" ? U : { kind: k, vlans: edit.tagged.kind === "unchanged" ? [] : edit.tagged.vlans });
            }}>
              <option value="unchanged">unchanged</option>
              <option value="replace">replace with…</option>
              <option value="add">add…</option>
              <option value="remove">remove…</option>
            </select>
          </span>
          {edit.tagged.kind !== "unchanged" && (
            <VlanChooser id="bulk-tagged" value={edit.tagged.vlans} onChange={(v) => up("tagged", { kind: edit.tagged.kind as TaggedVerb, vlans: v })} space={tags.space} status={tags.status} />
          )}
        </div>
        {/* Restates ADR-0078 decision 1 — a PoE field with no value is carried from the document the
            controller holds (`internal/generate/switchapply.go`), which replaces a PoE setting changed
            on the switch by hand. Until #1678 the choice read "leave the switch's own setting", its
            hint "Leaving the switch's own setting is not the same as off." and the preview's cell
            "switch's own"; note 4005663823 there corrected the three. */}
        <TriField<boolean> label="PoE" field={edit.poe} onChange={(f) => up("poe", f)} clearLabel="leave as the controller holds it"
          hint="Leaving it as the controller holds it is not the same as off.">
          <select aria-label="PoE: value" value={setValue(edit.poe) === undefined ? "" : setValue(edit.poe) ? "on" : "off"}
            onChange={(e) => up("poe", { kind: "set", value: e.target.value === "" ? undefined : e.target.value === "on" })}>
            <option value="">Choose one</option><option value="on">on</option><option value="off">off — turns the port's power off</option>
          </select>
        </TriField>
        <TriField<Priority> label="PoE priority" field={edit.poePriority} onChange={(f) => up("poePriority", f)} clearLabel="none">
          <select aria-label="PoE priority: value" value={setValue(edit.poePriority) ?? ""} onChange={(e) => up("poePriority", { kind: "set", value: e.target.value as Priority })}>
            <option value="">Choose one</option>{PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </TriField>
        <TriField<number> label="PoE power limit (mW)" field={edit.poeLimit} onChange={(f) => up("poeLimit", f)} clearLabel="none">
          <input type="number" min={1} aria-label="PoE power limit (mW): value" value={setValue(edit.poeLimit) ?? ""} onChange={(e) => up("poeLimit", { kind: "set", value: e.target.value === "" ? undefined : Number(e.target.value) })} />
        </TriField>
        <TriField<string> label="Description" field={edit.description} onChange={(f) => up("description", f)} clearLabel="clear">
          <input type="text" maxLength={128} aria-label="Description: value" value={setValue(edit.description) ?? ""} onChange={(e) => up("description", { kind: "set", value: e.target.value })} />
        </TriField>
        <TriField<boolean> label="Enabled" field={edit.enabled} onChange={(f) => up("enabled", f)} hint="Off stops the port passing traffic. The configuration plan refuses this on the uplink.">
          <select aria-label="Enabled: value" value={setValue(edit.enabled) === undefined ? "" : setValue(edit.enabled) ? "yes" : "no"}
            onChange={(e) => up("enabled", { kind: "set", value: e.target.value === "" ? undefined : e.target.value === "yes" })}>
            <option value="">Choose one</option><option value="yes">yes</option><option value="no">no</option>
          </select>
        </TriField>
      </div>
      <h4 className="bulk-preview-head">Preview</h4>
      {picked.length === 0
        ? <p className="muted small">No port selected. Select ports on the faceplate to see what this would change.</p>
        : (
          <div className="table-wrap"><table className="bulk-preview" aria-label="Preview">
            <thead><tr><th>Port</th>{FIELDS.map((f) => <th key={f}>{FIELD_LABEL[f]}</th>)}</tr></thead>
            <tbody>{rows.map((r) => (
              <tr key={r.port} className={r.differs.length ? "is-changed" : undefined}>
                <td>
                  <PortName name={r.port} />
                  {uplink?.includes(r.port) && <> <Pill tone="warn" title="The port the switch reports it reaches its controller through. The configuration plan refuses planned settings that would cut it off.">uplink</Pill></>}
                  {r.firstTime && (r.fromHeld
                    ? <> <Pill tone="none" title="This port has no planned settings yet. The values not set here are the ones the controller holds for it.">first settings, from held</Pill></>
                    : <> <Pill tone="none" title="This port has no planned settings yet. The values not set here take the editor's defaults.">first settings</Pill></>)}
                </td>
                {FIELDS.map((f) => {
                  const moved = r.differs.includes(f);
                  return (
                    <td key={f} className={moved ? "bulk-moved" : "dim"}>
                      {r.needsMode ? (f === "mode" ? <span className="muted">needs a mode</span> : "—")
                        : moved ? <><s>{cellWords(f, r.before, tags.space)}</s> → {cellWords(f, r.after, tags.space)}</> : cellWords(f, r.after, tags.space)}
                    </td>
                  );
                })}
              </tr>
            ))}</tbody>
          </table></div>
        )}
      {needMode.length > 0 && (
        <p className="refusal-why">{needMode.length === 1 ? "One port has" : `${needMode.length} ports have`} no planned settings yet: {needMode.map((p, i) => <span key={p}>{i > 0 && ", "}<code>{p}</code></span>)}. A port&rsquo;s first settings need a mode. Set Mode, or take {needMode.length === 1 ? "it" : "them"} out of the selection.</p>
      )}
      {blankFirst.length > 0 && !empty && (
        <p className="refusal-why" role="alert">
          {held?.status === "unavailable"
            ? <>What the controller holds for this switch&rsquo;s ports could not be read{held.problem?.message ? <>: {held.problem.message}</> : held.why ? <>: {held.why}</> : null}.</>
            : <>The configuration the controller holds for this switch declares no such port.</>}
          {" "}{blankFirst.map((p, i) => <span key={p}>{i > 0 && ", "}<code>{p}</code></span>)} {blankFirst.length === 1 ? "has" : "have"} no planned settings yet, so every field not set here is blank for {blankFirst.length === 1 ? "it" : "them"}. That blank is not what the port has. If you save it, it becomes all of the port&rsquo;s planned settings, and the next plan you apply replaces the port&rsquo;s VLANs and PoE with it.
        </p>
      )}
      {lane.problem && <ProblemBox problem={lane.problem} onDismiss={lane.clear} />}
      {lane.problem?.reason === "stale" && (
        <p className="hint">
          A port&rsquo;s planned settings changed after this page read them, so nothing was saved. Your values are kept.{" "}
          <button type="button" className="btn btn-quiet" onClick={onReload}>Read the ports again</button> and save once more.
        </p>
      )}
      <div className="actions">
        <button type="button" className="btn btn-primary" onClick={() => void apply()} disabled={!canApply}>
          {picked.length === 0 ? "No port selected"
            : empty ? "Nothing set — every field is unchanged"
              : missing.length ? `Waiting for ${missing.join(", ")}`
                : readingHeld ? "Loading what the controller holds"
                : needMode.length ? "Waiting for a mode"
                  : changed === 0 ? "Nothing would change"
                    : `${wording.verb} ${changed} ${plural(changed, "port")}`}
        </button>
        <Info label="About saving">This saves planned settings and sends nothing to the switch: {wording.afterwards}</Info>
        {!empty && <button type="button" className="btn" onClick={() => setEdit(EMPTY_EDIT)}>Reset fields</button>}
      </div>
    </section>
  );
}
